/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { CardContainer } from '@atlassian/company-hub-common';

import { CardSize, CardsLayout } from '../../../../types';
import type { CommonCardProps } from '../types';

const placeholderBackgroundColor = token('color.background.accent.gray.subtlest');

const styles = cssMap({
	content: {
		height: '100%',
	},
	image: {
		backgroundColor: placeholderBackgroundColor,
	},
	imageFixedHeightMedium: {
		height: '140px',
	},
	imageFixedHeightLarge: {
		height: '200px',
	},
	imageFlexibleHeight: {
		minHeight: '0px',
		flex: '1 1 0',
	},
	imageSmall: {
		minWidth: '28%',
	},
	body: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	bodySmall: {
		flex: '1 1 0',
	},
	itemCommon: {
		height: '8px',
		backgroundColor: placeholderBackgroundColor,
	},
	title: {
		width: '35px',
	},
	publishedDate: {
		width: '100%',
	},
	descriptionFirstLine: {
		width: '121px',
	},
	descriptionSecondLine: {
		width: '149px',
	},
	titleLarge: {
		width: '50px',
	},
	descriptionFirstLineLarge: {
		width: '200px',
	},
	descriptionSecondLineLarge: {
		width: '280px',
	},
});

type DynamicCardLoadingStateProps = Pick<CommonCardProps, 'cardDimensions'> & {
	cardSize?: CardSize;
	layout?: CardsLayout;
};

const ContentSmall = () => (
	<Flex xcss={styles.content}>
		<Box xcss={cx(styles.image, styles.imageSmall)} />

		<Stack space="space.050" xcss={cx(styles.body, styles.bodySmall)} alignBlock="center">
			<Box xcss={cx(styles.itemCommon, styles.title)} />
			<Box xcss={cx(styles.itemCommon, styles.publishedDate)} />
		</Stack>
	</Flex>
);

const Content = ({
	isImageFixedHeight,
	cardSize,
}: {
	cardSize?: CardSize;
	isImageFixedHeight?: boolean;
}) => {
	const isLargeCard = cardSize === CardSize.LARGE;

	return (
		<Flex direction="column" xcss={styles.content}>
			<Box
				xcss={cx(
					styles.image,
					isImageFixedHeight
						? isLargeCard
							? styles.imageFixedHeightLarge
							: styles.imageFixedHeightMedium
						: styles.imageFlexibleHeight,
				)}
			/>
			<Stack space="space.075" xcss={styles.body}>
				<Box xcss={cx(styles.itemCommon, isLargeCard ? styles.titleLarge : styles.title)} />
				<Box xcss={cx(styles.itemCommon, styles.publishedDate)} />
				<Box
					xcss={cx(
						styles.itemCommon,
						isLargeCard ? styles.descriptionFirstLineLarge : styles.descriptionFirstLine,
					)}
				/>
				<Box
					xcss={cx(
						styles.itemCommon,
						isLargeCard ? styles.descriptionSecondLineLarge : styles.descriptionSecondLine,
					)}
				/>
			</Stack>
		</Flex>
	);
};

export const DynamicCardLoadingState = ({
	cardDimensions,
	cardSize,
	layout,
}: DynamicCardLoadingStateProps) => {
	const { height, minHeight, maxHeight } = cardDimensions;
	const containerId = `${cardSize}-card-loading-state`;

	if (
		layout === CardsLayout.FIXED_IMAGE_HEIGHT &&
		cardSize !== CardSize.SMALL &&
		fg('company_hub_cards_v2')
	) {
		return (
			<CardContainer id={`${containerId}-v2`}>
				<div
					style={{
						minHeight,
						maxHeight,
					}}
				>
					<Content isImageFixedHeight cardSize={cardSize} />
				</div>
			</CardContainer>
		);
	}

	return (
		<CardContainer id={`${containerId}-v1`} cardHeight={height}>
			{cardSize === CardSize.SMALL ? <ContentSmall /> : <Content cardSize={cardSize} />}
		</CardContainer>
	);
};
